import React, { useState, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Slider } from '../components/ui/slider';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import SEO from '../components/SEO';
import {
  DollarSign,
  Users,
  Server,
  HardDrive,
  Globe,
  Bot,
  Search,
  Shield,
  Headphones,
  Eye,
  Heart,
  Code,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Building2,
  Calculator,
  AlertCircle,
  Zap,
  Scale,
  Crown,
  PiggyBank,
  FileText,
  Percent,
  Lightbulb,
  Info,
  Lock,
  Printer,
  Download
} from 'lucide-react';

import UtilityModelTab from '../components/BusinessFinancials/UtilityModelTab';
import InfrastructureTab from '../components/BusinessFinancials/InfrastructureTab';
import AdRevenueTab from '../components/BusinessFinancials/AdRevenueTab';
import FoundingClubTab from '../components/BusinessFinancials/FoundingClubTab';

const BusinessFinancials = () => {
  const { user, loading } = useAuth();
  
  // Wait for auth state to load
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Access control: VBB personnel only
  const isVBBPersonnel = user?.is_operator_admin;
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isVBBPersonnel) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-red-50 to-white">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Lock className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Access Restricted</h2>
            <p className="text-muted-foreground">
              This document is only accessible to VBB management personnel.
            </p>
            <Button className="mt-4" onClick={() => window.history.back()}>
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ==================== STATE ====================
  // Note: Hooks must be called before conditional returns, but we need them after auth check
  // So we'll render a wrapper component pattern
  
  return <BusinessFinancialsContent />;
};

// Separate content component to handle hooks properly
const BusinessFinancialsContent = () => {
  // ==================== STATE ====================
  const [selectedScale, setSelectedScale] = useState('100K');
  const [utilityMargin, setUtilityMargin] = useState(15); // 12-18% range
  const [costBasis, setCostBasis] = useState('full'); // 'direct' or 'full'
  const [customClubRevenue, setCustomClubRevenue] = useState(10000);
  const [foundingClubFee, setFoundingClubFee] = useState(1); // 1-3% range
  const [printMode, setPrintMode] = useState(false);

  // Print mode styles
  const printStyles = `
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .no-print { display: none !important; }
      .print-break { page-break-before: always; }
      .bg-gradient-to-b, .bg-gradient-to-r { background: white !important; }
      .bg-slate-800, .bg-slate-800\\/50 { background: #f8fafc !important; }
      .text-white { color: #1e293b !important; }
      .text-slate-300, .text-slate-400 { color: #475569 !important; }
      .border-slate-700 { border-color: #e2e8f0 !important; }
      nav, footer, .cookie-banner { display: none !important; }
    }
  `;

  // Inject print styles
  React.useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.innerHTML = printStyles;
    document.head.appendChild(styleEl);
    return () => document.head.removeChild(styleEl);
  }, [printStyles]);

  // ==================== COST DATA ====================
  const scaleData = {
    '10K': {
      users: '10,000',
      label: 'Early Stage',
      color: 'bg-green-500',
      infra: { compute: 50, database: 50, storage: 20, cdn: 20, ai: 10, search: 0, other: 20, total: 170 },
      labor: { support: 0, moderation: 0, community: 0, devops: 0, trustSafety: 0, total: 0 },
      staff: '1 (Founder)',
      perUser: 0.005
    },
    '100K': {
      users: '100,000',
      label: 'Growth',
      color: 'bg-blue-500',
      infra: { compute: 50, database: 50, storage: 30, cdn: 20, ai: 20, search: 0, other: 30, total: 200 },
      labor: { support: 2000, moderation: 1200, community: 2500, devops: 4000, trustSafety: 0, total: 9700 },
      staff: '3-4',
      perUser: 0.10
    },
    '500K': {
      users: '500,000',
      label: 'Scale',
      color: 'bg-purple-500',
      infra: { compute: 150, database: 200, storage: 150, cdn: 200, ai: 150, search: 100, other: 50, total: 1000 },
      labor: { support: 12000, moderation: 8000, community: 7500, devops: 10000, trustSafety: 6500, total: 44000 },
      staff: '8-12',
      perUser: 0.09
    },
    '1M': {
      users: '1,000,000',
      label: 'Mainstream',
      color: 'bg-amber-500',
      infra: { compute: 300, database: 500, storage: 300, cdn: 200, ai: 300, search: 200, other: 200, total: 2000 },
      labor: { support: 35000, moderation: 20000, community: 14000, devops: 23000, trustSafety: 16000, total: 108000 },
      staff: '18-25',
      perUser: 0.11
    },
    '10M': {
      users: '10,000,000',
      label: 'Enterprise',
      color: 'bg-red-500',
      infra: { compute: 2000, database: 5000, storage: 3000, cdn: 500, ai: 3000, search: 1000, other: 1500, total: 16000 },
      labor: { support: 300000, moderation: 185000, community: 70000, devops: 75000, trustSafety: 65000, total: 695000 },
      staff: '130-180',
      perUser: 0.07
    }
  };

  // ==================== AD REVENUE DATA ====================
  const adRevenueProjections = [
    { mau: 1000, pageViews: 15000, adImpressions: 12000, cpm: 2.5, rpm: 8.0, monthlyRevenue: 120, annualRevenue: 1440, status: 'beta' },
    { mau: 5000, pageViews: 75000, adImpressions: 60000, cpm: 3.0, rpm: 10.0, monthlyRevenue: 750, annualRevenue: 9000, status: 'growth' },
    { mau: 10000, pageViews: 150000, adImpressions: 120000, cpm: 3.5, rpm: 12.0, monthlyRevenue: 1800, annualRevenue: 21600, status: 'growth' },
    { mau: 50000, pageViews: 750000, adImpressions: 600000, cpm: 4.0, rpm: 15.0, monthlyRevenue: 11250, annualRevenue: 135000, status: 'scale' },
    { mau: 100000, pageViews: 1500000, adImpressions: 1200000, cpm: 5.0, rpm: 18.0, monthlyRevenue: 27000, annualRevenue: 324000, status: 'scale' }
  ];

  const adAssumptions = [
    { label: 'Avg Page Views per MAU', value: '15 pages/month' },
    { label: 'Ad Fill Rate', value: '80%' },
    { label: 'Ads per Page', value: '2-3 ads' },
    { label: 'Ad Network', value: 'Google AdSense' },
    { label: 'Audience Niche', value: 'Pet/Dog owners (premium CPM)' },
    { label: 'Guest vs Member Split', value: '70% guest (ads) / 30% members (no ads)' }
  ];

  // ==================== UTILITY MODEL CALCULATIONS ====================
  const current = scaleData[selectedScale];
  const directCosts = current.infra.total;
  const fullLoadedCosts = current.infra.total + current.labor.total;
  const baseCost = costBasis === 'direct' ? directCosts : fullLoadedCosts;
  
  // Utility model: Cost + Margin
  const utilityFee = useMemo(() => {
    return baseCost * (1 + utilityMargin / 100);
  }, [baseCost, utilityMargin]);

  // Custom club calculator
  const customClubUtilityFee = useMemo(() => {
    // Proportional cost allocation based on club size relative to platform
    const platformUsers = parseInt(selectedScale.replace('K', '000').replace('M', '000000'));
    const clubShareOfPlatform = 0.1; // Assume club is 10% of platform for estimation
    const clubProportionalCost = baseCost * clubShareOfPlatform;
    return clubProportionalCost * (1 + utilityMargin / 100);
  }, [baseCost, utilityMargin, selectedScale]);

  // Founding club remuneration calculation
  const foundingClubRemuneration = useMemo(() => {
    return customClubRevenue * (foundingClubFee / 100);
  }, [customClubRevenue, foundingClubFee]);

  // ==================== COMPARISON DATA ====================
  const modelComparison = useMemo(() => {
    const platformRevenue = customClubRevenue * 10; // Assume 10 clubs
    
    return [
      {
        model: 'Current (45% Revenue Share)',
        vbbShare: customClubRevenue * 0.45,
        clubRetains: customClubRevenue * 0.55,
        pros: ['Simple to understand', 'Aligns VBB with club success'],
        cons: ['Opaque cost structure', 'High perceived take rate', 'Clubs may feel exploited as they grow'],
        color: 'bg-amber-500'
      },
      {
        model: `Utility Model (Cost + ${utilityMargin}%)`,
        vbbShare: customClubUtilityFee,
        clubRetains: customClubRevenue - customClubUtilityFee,
        pros: ['Transparent costs', 'Regulated profit margin', 'Builds trust', 'Auditable'],
        cons: ['More complex accounting', 'VBB profit capped', 'Requires cost tracking'],
        color: 'bg-blue-500'
      },
      {
        model: 'Hybrid (Utility + IP Royalty)',
        vbbShare: customClubUtilityFee + (customClubRevenue * 0.03),
        clubRetains: customClubRevenue - customClubUtilityFee - (customClubRevenue * 0.03),
        pros: ['Transparent operations cost', 'Recognizes IP value', 'Future-proof'],
        cons: ['Two fee components', 'Perpetual royalty may concern some'],
        color: 'bg-purple-500'
      }
    ];
  }, [customClubRevenue, customClubUtilityFee, utilityMargin]);

  // ==================== HELPERS ====================
  const formatCurrency = (amount) => {
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
    return `$${amount.toFixed(0)}`;
  };

  const getStatusBadge = (status) => {
    const configs = {
      beta: { label: 'Beta', className: 'bg-blue-500' },
      growth: { label: 'Growth', className: 'bg-green-500' },
      scale: { label: 'Scale', className: 'bg-purple-500' }
    };
    const config = configs[status] || configs.beta;
    return <Badge className={`${config.className} text-white text-xs`}>{config.label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="business-financials-page">
      <SEO 
        title="Business Financials - Revenue & Cost Analysis"
        description="Financial projections, cost analysis, and business models for the AADC platform."
        keywords="business plan, revenue projections, cost analysis, utility model"
      />

      {/* Hero Section */}
      <section className="py-12 px-4 bg-gradient-to-r from-slate-800 to-slate-900 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
              <PiggyBank className="w-7 h-7" />
            </div>
            <div>
              <Badge className="mb-2 bg-amber-500/20 text-amber-300">Investor Document</Badge>
              <h1 className="text-3xl md:text-4xl font-bold">Business Financials Review</h1>
            </div>
          </div>
          <p className="text-slate-300 text-lg max-w-2xl">
            Comprehensive analysis of platform costs, revenue projections, and business model options. 
            This document is for internal review and investor presentations.
          </p>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="px-4 py-8">
        <div className="container mx-auto max-w-6xl">
          <Tabs defaultValue="utility-model" className="space-y-6">
            <TabsList className="grid w-full grid-cols-4 lg:grid-cols-4">
              <TabsTrigger value="utility-model" className="gap-2">
                <Scale className="w-4 h-4 hidden sm:inline" />
                Utility Model
              </TabsTrigger>
              <TabsTrigger value="costs" className="gap-2">
                <Server className="w-4 h-4 hidden sm:inline" />
                Infrastructure
              </TabsTrigger>
              <TabsTrigger value="revenue" className="gap-2">
                <TrendingUp className="w-4 h-4 hidden sm:inline" />
                Ad Revenue
              </TabsTrigger>
              <TabsTrigger value="founding" className="gap-2">
                <Crown className="w-4 h-4 hidden sm:inline" />
                Founding Club
              </TabsTrigger>
            </TabsList>

            {/* ==================== UTILITY MODEL TAB ==================== */}
            <TabsContent value="utility-model" className="space-y-6">
              <UtilityModelTab scaleData={scaleData} selectedScale={selectedScale} setSelectedScale={setSelectedScale} utilityMargin={utilityMargin} setUtilityMargin={setUtilityMargin} costBasis={costBasis} setCostBasis={setCostBasis} customClubRevenue={customClubRevenue} setCustomClubRevenue={setCustomClubRevenue} baseCost={baseCost} utilityFee={utilityFee} modelComparison={modelComparison} formatCurrency={formatCurrency} />
            </TabsContent>

            {/* ==================== INFRASTRUCTURE COSTS TAB ==================== */}
            <TabsContent value="costs" className="space-y-6">
              <InfrastructureTab scaleData={scaleData} selectedScale={selectedScale} setSelectedScale={setSelectedScale} current={current} formatCurrency={formatCurrency} />
            </TabsContent>

            {/* ==================== AD REVENUE TAB ==================== */}
            <TabsContent value="revenue" className="space-y-6">
              <AdRevenueTab adRevenueProjections={adRevenueProjections} adAssumptions={adAssumptions} getStatusBadge={getStatusBadge} />
            </TabsContent>

            {/* ==================== FOUNDING CLUB TAB ==================== */}
            <TabsContent value="founding" className="space-y-6">
              <FoundingClubTab customClubRevenue={customClubRevenue} setCustomClubRevenue={setCustomClubRevenue} foundingClubFee={foundingClubFee} setFoundingClubFee={setFoundingClubFee} utilityMargin={utilityMargin} customClubUtilityFee={customClubUtilityFee} foundingClubRemuneration={foundingClubRemuneration} formatCurrency={formatCurrency} />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Document Footer */}
      <section className="px-4 pb-16 no-print">
        <div className="container mx-auto max-w-6xl">
          <Card className="bg-slate-50">
            <CardContent className="pt-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Document Version: 1.0</p>
                  <p className="text-sm text-muted-foreground">Last Updated: {new Date().toLocaleDateString()}</p>
                  <p className="text-sm text-muted-foreground">Confidential - VBB Internal Use Only</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => window.print()}>
                    <Printer className="w-4 h-4 mr-2" />
                    Print
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      // Open print dialog with PDF save hint
                      const printInstructions = window.confirm(
                        'To save as PDF:\n\n' +
                        '1. Click OK to open print dialog\n' +
                        '2. Select "Save as PDF" as your printer\n' +
                        '3. Click Save\n\n' +
                        'Continue?'
                      );
                      if (printInstructions) window.print();
                    }}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Save as PDF
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Print Header - Only visible when printing */}
      <div className="hidden print:block print:mb-8">
        <h1 className="text-2xl font-bold">VBB Business Financials Review</h1>
        <p className="text-sm text-gray-600">Generated: {new Date().toLocaleString()}</p>
        <p className="text-sm text-gray-600">Confidential - Internal Use Only</p>
      </div>
    </div>
  );
};

export default BusinessFinancials;
